import { CommonModule } from '@angular/common';
import { Component, inject, computed } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { Dashboard } from 'src/app/dashboard/models/dashboard';
import { DashboardStore } from 'src/app/dashboard/stores/dashboard.store';
import { NectaModule } from 'src/app/necta-module/models/necta-module.model';
import { NectaModuleStore } from 'src/app/necta-module/store/necta-module.store';
import { AuthService } from 'src/app/shared/services/auth-service.service';
import { NectaUser, NectaUserRole, UserProfile } from 'src/app/shared/user/models/user';
import { PermissionCode } from 'src/app/shared/user/models/permission';
import { UserStore } from 'src/app/shared/user/store/user.store';
import { AppMenuitemComponent } from '../../app-menuitem/app-menuitem.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-main-menu',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    AppMenuitemComponent
],
  templateUrl: './main-menu.component.html',
  styleUrl: './main-menu.component.css'
})
export class MainMenuComponent{
  private readonly router = inject(Router);
  private readonly dashboardStore = inject(DashboardStore);
  private readonly moduleStore = inject(NectaModuleStore);
  private readonly userStore = inject(UserStore);
  private readonly authService = inject(AuthService);

  private vendorCatalogEnabled = this.userStore.isRegisteredNectaVendorCatalog;
  private nectaOneApiEnabled = this.userStore.isRegisteredNectaOne;
  // Registriert heisst nur "NectaOne-Account vorhanden" – ohne Mandanten-Einladung gibt es
  // keine Warenwirtschaft anzuzeigen (sonst leerer Menue-Eintrag).
  private hasNectaTenants = computed(() => (this.userStore.userProfile()?.nectaUsers?.length ?? 0) > 0);
  private isSuperAdmin = this.authService.isSuperAdmin;
  private isNectaSystemAdministrator = this.authService.isNectaSystemAdministrator;

  /** Fuer Menue-Konfiguration verfuegbar: `permissions: [this.PermissionCode.Reports]` statt Hardcoded-Strings. */
  protected readonly PermissionCode = PermissionCode;

  dashboardItems = computed(() => this.updateDashboardItems(this.dashboardStore.dashboards()));
  nectaUserItems = computed(() => this.updateNectaItems(this.userStore.activeUserProfile() as UserProfile));
  moduleItems = computed(() => this.updateModuleItems(this.moduleStore.nectaModules()!));
  adminItems = computed(() => this.updateAdminItems());

  // Signal for menu items
  //
  // Berechtigungsbasiertes Ein-/Ausblenden:
  // - Top-Level-Eintrag  -> `visible:` setzen (Template rendert nur bei visible !== false).
  // - Submenue-Eintrag   -> `permissions: [...]` setzen (wird vom isVisibleByAccess-Filter
  //                         der update*Items-Builder ausgewertet); nur EIN einheitliches Recht.
  //
  // Einheitliches Recht ueber mehrere Codes:
  //   visible: this.userStore.hasAnyPermission([this.PermissionCode.Reports, this.PermissionCode.Orders]),
  //   visible: this.userStore.hasPermission(this.PermissionCode.Orders, PermissionRight.ReadWrite),
  //
  // Unterschiedliche Rechte je Code -> einzelne hasPermission(code, right) mit && / || verknuepfen
  // (die Array-Helfer koennen nur EIN minRight fuer alle Codes). Beispiel: Orders braucht
  // ReadWrite, Reports nur Read:
  //   visible: this.userStore.hasPermission(this.PermissionCode.Orders, PermissionRight.ReadWrite)
  //         && this.userStore.hasPermission(this.PermissionCode.Reports, PermissionRight.Read),
  // (fuer PermissionRight ggf. den Import in permission.ts erweitern; PermissionCode ist als
  //  this.PermissionCode bereits verfuegbar.)
  //
  // menuItems ist ein computed() -> die Sichtbarkeit aktualisiert sich reaktiv bei
  // Tenant-Wechsel / nachgeladenen Permissions.
  menuItems: any = computed(() => [
    this.buildDashboardMenuEntry(this.dashboardItems()),
    {
      icon: 'qassandra',
      expanded: false,
      label: 'Qassandra',
      visible: this.userStore.hasPermission(this.PermissionCode.Qassandra), // mind. Read auf 'qassandra'
      routerLink: '/qassandra',
      command: () => {
        this.router.navigate(['/qassandra']);
      }
    },
    {
      icon: 'truck',
      expanded: false,
      label: 'necta.supply',
      visible: this.vendorCatalogEnabled() === true,
      routerLink: '/necta-supply',
      command: () => {
        this.router.navigate(['/necta-supply']);
      }
    },
    {
      icon: 'building-store',
      expanded: false,
      label: 'necta Warenwirtschaft',
      visible: this.nectaOneApiEnabled() === true && this.hasNectaTenants(),
      items: this.nectaUserItems(),
    },
    {
      icon: 'building-store',
      expanded: false,
      label: 'ClientApps-Verwaltung',
      visible: this.isSuperAdmin() ,
      routerLink: '/client-application-management',
      command: () => {
        this.router.navigate(['/client-application-management']);
      }
    },
    ...this.moduleItems(),
    {
      icon: 'cpu',
      expanded: false,
      label: 'Administration',
      visible: this.userStore.isActiveUserSysAdmin(),
      items: this.adminItems(),
    },
    
  ]);

  /**
   * Baut den Dashboards-Eintrag der Sidebar. Bei genau einem sichtbaren Sub-Item wird
   * dieses als direkter Menüpunkt promoviert (kein Submenü). Ab zwei Items bleibt es
   * der Aufklapp-Eintrag "Dashboards".
   */
  private buildDashboardMenuEntry(items: { label: string; icon: string; routerLink: string; roles: NectaUserRole[] }[]): MenuItem {
    if (items.length === 1) {
      const only = items[0];
      return {
        icon: only.icon ?? 'chart-bar',
        label: only.label,
        routerLink: only.routerLink,
        command: () => this.router.navigateByUrl(only.routerLink),
      };
    }
    return {
      icon: 'chart-bar',
      label: 'Dashboards',
      expanded: false,
      items,
    };
  }

  /**
   * Sichtbarkeit eines Menuepunkts anhand Rollen UND Permissions (beide optional, UND-verknuepft).
   * Ohne `roles`/`permissions` ist der Punkt sichtbar. `permissions` verwendet die
   * Client-Permission-Codes (siehe `PermissionCode`), OR-Semantik innerhalb der Liste.
   */
  private isVisibleByAccess(item: { roles?: NectaUserRole[]; permissions?: string[] }): boolean {
    const roleOk = !item.roles || this.userStore.hasAnyRole(item.roles);
    const permOk = !item.permissions || this.userStore.hasAnyPermission(item.permissions);
    return roleOk && permOk;
  }

  private updateDashboardItems(dashboards: Dashboard[]) {
    const managementEnabled = (environment as any).dashboardManagementEnabled === true;

    let items;
    if (!this.nectaOneApiEnabled() || !managementEnabled) {
      items = [...dashboards.map(dashboard => ({
        label: dashboard.name,
        icon: dashboard.icon,
        routerLink: dashboard.id === -1 ? '/dashboard/welcome' : `/dashboard/${dashboard.id}/view`,
        roles: []
      }))];
    } else {
      items = [
        ...dashboards.map(dashboard => ({
          label: dashboard.name,
          icon: dashboard.icon,
          routerLink: dashboard.id === -1 ? '/dashboard/welcome' : `/dashboard/${dashboard.id}/view`,
          roles: []
        })),
        { label: 'Alle Dashboards', icon: 'list', routerLink: '/dashboard/list', roles: [NectaUserRole.Sysadmin] },
        { label: 'Neues Dashboard', icon: 'plus', routerLink: '/dashboard/create', roles: [NectaUserRole.Sysadmin] }
      ];
    }
    return items.filter(item => this.isVisibleByAccess(item));
  }

  private updateNectaItems(userProfile: UserProfile) {
    const users = userProfile?.nectaUsers ?? [];
    let tenants: {tenantId: number, tenantName: string}[] = [];
    userProfile?.nectaUsers?.forEach(u => {
      if(!tenants.some(x => u.tenantId == x.tenantId))
        tenants.push({tenantId: u.tenantId, tenantName: u.tenantDescription})
    })

    if(tenants.length > 1) {
      return tenants.map(tenant => ({
        label: tenant.tenantName,
        icon: 'building',
        expanded: true,
        items: this.getNectaUserItems(users.filter(u => u.tenantId == tenant.tenantId))
      }));
    } else {
      return this.getNectaUserItems(users);
    }
   
   
  }

  private updateModuleItems(modules: NectaModule[]) {
    let items = [
        ...modules.map(module => ({
          label: module.name,
          icon: "pi " + module.icon,
          routerLink: `/necta-modules/${module.id}/view`,
          command: () => {
            this.router.navigate([`/necta-modules/${module.id}/view`]);
          },
          roles: []
        }))
      ];
    //return items;
    return items.filter(item => this.isVisibleByAccess(item));
   
  }

   private updateAdminItems(): MenuItem[] {
    let items = [
        { label: 'Module', icon: 'cpu', routerLink: '/necta-modules/list', roles: [NectaUserRole.Sysadmin] }
      ];
    //return items;
    return items.filter(item => this.isVisibleByAccess(item));
   
  }


  private getNectaUserItems(users: NectaUser[]) {
    return [...users.map(user => ({
        label: this.getUserGroupTypeName(user.userGroupType) +" (" + user.nectaUserCode +")",
        icon: 'user',
        routerLink: `/necta/${user.nectaUserId}/view`,
        roles: []
      }))];
    }

  private getUserGroupTypeName(userGroupType: NectaUserRole): string {
    switch (userGroupType) {
      case NectaUserRole.User:
        return 'User';
      case NectaUserRole.Controlling:
        return 'Controlling';
      case NectaUserRole.Sysadmin:
        return 'Sysadmin';
      case NectaUserRole.Mbs:
        return 'MBS';
      default:
        return 'Unknown';
  }
}
}
